import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronRight, LifeBuoy, MessageSquare, Plus } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { EmptyState } from '@/components/common/EmptyState'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { SectionLoader } from '@/components/common/SectionLoader'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { formatDateTime, formatRelative } from '@/lib/formatters'
import { useSupportTickets } from '@/features/support/hooks/useSupportTickets'
import { categoryLabel } from '@/features/support/supportLabels'
import { NewTicketModal } from './NewTicketModal'
import { TicketStatusBadge } from './TicketStatusBadge'
import type { TicketSummary } from '@/types/support-ticket.types'

function TicketRow({
  ticket,
  showRequester,
}: {
  ticket: TicketSummary
  showRequester: boolean
}): React.ReactElement {
  const needsAttention = ticket.status === 'pending'
  const activityAt = ticket.lastMessageAt ?? ticket.createdAt
  return (
    <li>
      <Link
        to={`/support/${ticket.id}`}
        className={cn(
          'group flex min-w-0 items-center gap-3 border-l-2 px-4 py-3.5 transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none dark:hover:bg-gray-800/60 dark:focus-visible:bg-gray-800/60 sm:px-5',
          needsAttention
            ? 'border-l-primary bg-primary/5 dark:bg-primary/10'
            : 'border-l-transparent'
        )}
      >
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="shrink-0 font-mono text-xs text-gray-500 dark:text-gray-400">
              {ticket.reference}
            </span>
            <TicketStatusBadge status={ticket.status} />
          </div>
          <p
            className={cn(
              'truncate text-sm text-gray-900 dark:text-gray-100',
              needsAttention ? 'font-semibold' : 'font-medium'
            )}
          >
            {ticket.subject}
          </p>
          <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
            {showRequester && (
              <span className="max-w-full truncate" title={ticket.createdByEmail}>
                {ticket.createdByName}
              </span>
            )}
            {showRequester && <span aria-hidden>·</span>}
            <span>{categoryLabel(ticket.category)}</span>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1">
              <MessageSquare className="h-3 w-3" aria-hidden />
              {ticket.messageCount}
              <span className="sr-only"> messages</span>
            </span>
            {activityAt && (
              <>
                <span aria-hidden>·</span>
                <time dateTime={activityAt} title={formatDateTime(activityAt)}>
                  {formatRelative(activityAt)}
                </time>
              </>
            )}
          </p>
        </div>
        <ChevronRight
          className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </Link>
    </li>
  )
}

export function SupportPage(): React.ReactElement {
  const navigate = useNavigate()
  const { data, isLoading, isError, error, refetch } = useSupportTickets()
  const [createOpen, setCreateOpen] = useState(false)

  const tickets = data?.items ?? []
  const seesWholeOrganization = data?.seesWholeOrganization === true
  const needsReply = tickets.filter((t) => t.status === 'pending').length

  const newTicketButton = (
    <Button type="button" variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
      <Plus className="h-4 w-4" aria-hidden />
      <span className="ml-1.5">New ticket</span>
    </Button>
  )

  return (
    <>
      <PageShell
        breadcrumbItems={[{ label: 'Settings', to: '/dashboard' }, { label: 'Help & support' }]}
        icon={LifeBuoy}
        title="Help & support"
        description="Ask the KAAMCO team a question or report a problem."
        className="gap-0"
        hideHeader
      >
        <div className="space-y-4">
          <WorkspaceToolbar
            filter={
              <div className="flex min-w-0 items-center gap-2">
                <LifeBuoy className="h-4 w-4 shrink-0 text-primary dark:text-primary-light" aria-hidden />
                <h1 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {seesWholeOrganization ? 'Organization tickets' : 'Your tickets'}
                </h1>
              </div>
            }
            actions={newTicketButton}
          />

          {needsReply > 0 && (
            <p className="rounded-lg border border-primary/20 bg-primary/10 px-4 py-2.5 text-sm text-gray-800 dark:text-gray-100">
              <span className="font-semibold text-primary dark:text-primary-light">
                {needsReply === 1 ? '1 ticket has' : `${needsReply} tickets have`} a reply from
                support.
              </span>{' '}
              Open it to read the answer or follow up.
            </p>
          )}

          {isLoading ? (
            <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <SectionLoader variant="list" rows={4} label="Loading support tickets" />
            </div>
          ) : isError ? (
            <QueryErrorState
              error={error}
              title="Couldn't load your support tickets"
              onRetry={refetch}
            />
          ) : tickets.length === 0 ? (
            <EmptyState
              icon={LifeBuoy}
              title="No support tickets yet"
              description="Stuck on something or have an idea? Our team is happy to help."
              action={newTicketButton}
            />
          ) : (
            <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white dark:divide-gray-700/70 dark:border-gray-700 dark:bg-gray-800">
              {tickets.map((ticket) => (
                <TicketRow
                  key={ticket.id}
                  ticket={ticket}
                  showRequester={seesWholeOrganization}
                />
              ))}
            </ul>
          )}
        </div>
      </PageShell>

      <NewTicketModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(ticket) => {
          setCreateOpen(false)
          navigate(`/support/${ticket.id}`)
        }}
      />
    </>
  )
}

SupportPage.displayName = 'SupportPage'
